import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, CalendarDays } from 'lucide-react';
import { api } from '@/api/client';
import { SearchableSelect } from '@/components/ui/SearchableSelect';
import { dateTime, useLeadUsers } from './leads.api';
import { LeadDetailDrawer } from './LeadDetailDrawer';
import { LeadsSubNav } from './LeadsSubNav';

/**
 * Calendar — the month, with every planned follow-up and expected close date on it.
 *
 * Built from plain date arithmetic rather than a calendar library: it renders one month of a fixed
 * six-week grid, which is all this needs and adds nothing to the bundle.
 */
interface CalEvent {
  id: string;
  kind: 'FOLLOWUP' | 'CLOSE';
  date: string;
  title: string;
  leadId: number;
  leadNo: string;
  company: string;
  mobile: string | null;
  type: string;
  status: string | null;
  notes: string | null;
  overdue: boolean;
}

const DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export function LeadsCalendarPage() {
  const { data: users } = useLeadUsers();
  const [cursor, setCursor] = useState(() => { const d = new Date(); d.setDate(1); d.setHours(0, 0, 0, 0); return d; });
  const [assignedToId, setAssignedToId] = useState<string | number | ''>('');
  const [detailId, setDetailId] = useState<number | null>(null);
  const [openDay, setOpenDay] = useState<string | null>(null);

  // The visible grid always starts on the Monday on/before the 1st and runs 42 days, so a month
  // that starts on a Sunday does not push its last week off the bottom.
  const gridStart = useMemo(() => {
    const d = new Date(cursor);
    const dow = (d.getDay() + 6) % 7;   // Mon = 0
    d.setDate(d.getDate() - dow);
    return d;
  }, [cursor]);
  const gridEnd = useMemo(() => { const d = new Date(gridStart); d.setDate(d.getDate() + 41); return d; }, [gridStart]);

  const { data } = useQuery({
    queryKey: ['lead-calendar', iso(gridStart), iso(gridEnd), assignedToId],
    queryFn: async () => (await api.get<{ events: CalEvent[] }>('/leads/calendar', {
      params: { from: iso(gridStart), to: iso(gridEnd), ...(assignedToId ? { assignedToId } : {}) },
    })).data,
  });

  const byDay = useMemo(() => {
    const m = new Map<string, CalEvent[]>();
    for (const e of data?.events ?? []) {
      const k = iso(new Date(e.date));
      const arr = m.get(k) ?? [];
      arr.push(e);
      m.set(k, arr);
    }
    return m;
  }, [data]);

  const days = useMemo(() => Array.from({ length: 42 }, (_, i) => { const d = new Date(gridStart); d.setDate(d.getDate() + i); return d; }), [gridStart]);
  const todayKey = iso(new Date());

  return (
    <div className="p-6">
      <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold">Calendar</h1>
          <p className="text-xs text-gray-500 mt-0.5">Planned follow-ups and expected close dates, on one month view.</p>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <label className="text-xs text-gray-500 block">Assigned to</label>
            <SearchableSelect value={assignedToId} onChange={setAssignedToId} allowClear placeholder="Everyone"
              options={(users ?? []).map((u) => ({ value: u.id, label: u.name }))} className="min-w-[170px]" />
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => setCursor((c) => { const d = new Date(c); d.setMonth(d.getMonth() - 1); return d; })} className="p-2 border rounded"><ChevronLeft size={14} /></button>
            <div className="px-3 py-1.5 text-sm font-medium min-w-[150px] text-center">
              {cursor.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}
            </div>
            <button onClick={() => setCursor((c) => { const d = new Date(c); d.setMonth(d.getMonth() + 1); return d; })} className="p-2 border rounded"><ChevronRight size={14} /></button>
            <button onClick={() => { const d = new Date(); d.setDate(1); d.setHours(0, 0, 0, 0); setCursor(d); }}
              className="px-3 py-1.5 border rounded text-xs inline-flex items-center gap-1"><CalendarDays size={13} /> Today</button>
          </div>
        </div>
      </div>

      <LeadsSubNav />

      <div className="bg-white dark:bg-slate-900 border rounded overflow-hidden">
        <div className="grid grid-cols-7 bg-slate-50 dark:bg-slate-800 text-xs font-medium text-slate-600 dark:text-slate-300">
          {DOW.map((d) => <div key={d} className="px-2 py-2 text-center border-b">{d}</div>)}
        </div>
        <div className="grid grid-cols-7">
          {days.map((d) => {
            const key = iso(d);
            const evts = byDay.get(key) ?? [];
            const otherMonth = d.getMonth() !== cursor.getMonth();
            return (
              <div key={key}
                className={`min-h-[104px] border-b border-r p-1.5 ${otherMonth ? 'bg-slate-50/60 dark:bg-slate-800/40' : ''} ${key === todayKey ? 'ring-1 ring-inset ring-emerald-400' : ''}`}>
                <div className={`text-[11px] mb-1 ${otherMonth ? 'text-gray-300' : 'text-gray-500'} ${key === todayKey ? 'font-semibold text-emerald-700' : ''}`}>
                  {d.getDate()}
                </div>
                <div className="space-y-0.5">
                  {evts.slice(0, 3).map((e) => (
                    <button key={e.id} onClick={() => setDetailId(e.leadId)}
                      title={`${e.title}${e.notes ? ` — ${e.notes}` : ''}`}
                      className={`w-full text-left text-[10px] px-1 py-0.5 rounded truncate ${
                        e.kind === 'CLOSE' ? 'bg-indigo-50 text-indigo-700'
                          : e.status === 'DONE' ? 'bg-emerald-50 text-emerald-700'
                            : e.overdue ? 'bg-rose-50 text-rose-700' : 'bg-amber-50 text-amber-700'}`}>
                      {e.kind === 'CLOSE' ? '★ ' : ''}{e.company}
                    </button>
                  ))}
                  {evts.length > 3 && (
                    <button onClick={() => setOpenDay(key)} className="text-[10px] text-slate-500 hover:underline">+{evts.length - 3} more</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 mt-2 text-[11px] text-gray-500">
        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-50 border border-amber-200" /> Planned</span>
        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-rose-50 border border-rose-200" /> Overdue</span>
        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-50 border border-emerald-200" /> Done</span>
        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-indigo-50 border border-indigo-200" /> ★ Expected close</span>
      </div>

      {openDay && (
        <div className="fixed inset-0 z-[880] bg-black/40 flex items-center justify-center p-4" onClick={() => setOpenDay(null)}>
          <div className="bg-white dark:bg-slate-900 rounded-lg shadow-xl w-full max-w-lg max-h-[70vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="px-4 py-3 border-b font-semibold text-sm">
              {new Date(openDay).toLocaleDateString('en-IN', { dateStyle: 'full' })}
            </div>
            <ul className="divide-y">
              {(byDay.get(openDay) ?? []).map((e) => (
                <li key={e.id} className="px-4 py-2 text-sm hover:bg-slate-50 cursor-pointer" onClick={() => { setOpenDay(null); setDetailId(e.leadId); }}>
                  <div className="font-medium">{e.company}</div>
                  <div className="text-xs text-gray-500">{e.kind === 'CLOSE' ? 'Expected close' : `${e.type} · ${dateTime(e.date)}`}{e.notes ? ` — ${e.notes}` : ''}</div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {detailId && <LeadDetailDrawer leadId={detailId} onClose={() => setDetailId(null)} />}
    </div>
  );
}
